import { API } from 'aws-amplify';

/**
 * Rewrite Contentful asset URLs to use the API Gateway endpoint
 * @param {string} url - The original URL
 * @returns {string} The rewritten URL
 */
function rewriteAssetUrl(url) {
  if (!url) return url;

  // Match Contentful asset URLs (images.ctfassets.net, assets.ctfassets.net, downloads.ctfassets.net, etc.)
  // Also match assets.contentful.com URLs
  const contentfulRegex = /^(https?:)?\/\/(images|assets|downloads|videos)\.(ctfassets\.net|contentful\.com)\/([^/]+)\/(.+)$/;
  const match = url.match(contentfulRegex);

  if (match) {
    // Extract the space ID and asset path
    const assetPath = match[5]; // Everything after the space ID

    // Get the API endpoint from Amplify config
    try {
      // Try to get from Amplify API configuration
      const apiConfig = API._config || API.configure();
      let endpoint = null;

      if (apiConfig && apiConfig.endpoints) {
        const contentEndpoint = apiConfig.endpoints.find(e => e.name === 'content');
        if (contentEndpoint && contentEndpoint.endpoint) {
          endpoint = contentEndpoint.endpoint;
        }
      }

      // If we have an endpoint, rewrite the URL
      if (endpoint) {
        return `${endpoint}/content/assets/${assetPath}`;
      } else {
        console.warn('No API endpoint found for content API, cannot rewrite URL:', url);
      }
    } catch (error) {
      console.error('Error accessing API config:', error);
    }
  }

  // If no match or config not available, return original URL
  return url;
}

/**
 * Recursively rewrite asset URLs in an object
 * @param {object} obj - The object to process
 */
function rewriteUrlsInObject(obj) {
  if (!obj || typeof obj !== 'object') return;

  // Handle arrays
  if (Array.isArray(obj)) {
    obj.forEach(item => rewriteUrlsInObject(item));
    return;
  }

  // Handle objects
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];

      // If it's a URL string, rewrite it
      if (typeof value === 'string' && (value.includes('ctfassets.net') || value.includes('contentful.com'))) {
        obj[key] = rewriteAssetUrl(value);
      }
      // Recursively process nested objects and arrays
      else if (typeof value === 'object' && value !== null) {
        rewriteUrlsInObject(value);
      }
    }
  }
}

/**
 * Recursively hydrates a Contentful entry with linked assets and entries.
 * - Hydrates image fields from includes.Asset
 * - Hydrates callToActionArticle fields from includes.Entry (recursively)
 * - Hydrates sections, sections2, sections3 arrays from includes.Entry (recursively)
 * - Hydrates relatedItems array from includes.Entry (recursively)
 * - Hydrates imageGallery array from includes.Asset
 * - Rewrites all Contentful asset URLs to use the API Gateway endpoint
 *
 * @param {object} entry - The Contentful entry to hydrate
 * @param {object} includes - The includes object from the Contentful API response
 * @returns {object} The hydrated entry
 */
export function hydrateEntry(entry, includes) {
  if (!entry || !entry.fields) return entry;

  // Hydrate image field from includes.Asset
  if (entry.fields.image && entry.fields.image.sys && includes.Asset) {
    const asset = includes.Asset.find(a => a.sys.id === entry.fields.image.sys.id);
    if (asset) entry.fields.image.fields = asset.fields;
  }

  // Hydrate callToActionArticle from includes.Entry (recursively)
  if (entry.fields.callToActionArticle && entry.fields.callToActionArticle.sys && includes.Entry) {
    const linkedEntry = includes.Entry.find(e => e.sys.id === entry.fields.callToActionArticle.sys.id);
    if (linkedEntry) {
      entry.fields.callToActionArticle.fields = linkedEntry.fields;
      hydrateEntry(entry.fields.callToActionArticle, includes);
    }
  }

  // Hydrate sections, sections2, sections3 arrays from includes.Entry (recursively)
  ['sections', 'sections2', 'sections3'].forEach(fieldName => {
    if (Array.isArray(entry.fields[fieldName]) && includes.Entry) {
      entry.fields[fieldName] = entry.fields[fieldName].map(linkedSection => {
        if (linkedSection.sys && linkedSection.sys.id) {
          const hydrated = includes.Entry.find(e => e.sys.id === linkedSection.sys.id);
          if (hydrated) {
            hydrateEntry(hydrated, includes);
            return hydrated;
          }
        }
        return linkedSection;
      });
    }
  });

  // Hydrate imageGallery array from includes.Asset
  if (Array.isArray(entry.fields.imageGallery) && includes.Asset) {
    entry.fields.imageGallery = entry.fields.imageGallery.map(linkedAsset => {
      if (linkedAsset.sys && linkedAsset.sys.id) {
        const asset = includes.Asset.find(a => a.sys.id === linkedAsset.sys.id);
        if (asset) {
          linkedAsset.fields = asset.fields;
          return linkedAsset;
        }
      }
      return linkedAsset;
    });
  }

  // Hydrate relatedItems array from includes.Entry (recursively)
  if (Array.isArray(entry.fields.relatedArticles) && includes.Entry) {
    entry.fields.relatedArticles = entry.fields.relatedArticles.map(linkedItem => {
      if (linkedItem.sys && linkedItem.sys.id) {
        const hydrated = includes.Entry.find(e => e.sys.id === linkedItem.sys.id);
        if (hydrated) {
          hydrateEntry(hydrated, includes);
          return hydrated;
        }
      }
      return linkedItem;
    });
  }

  // Hydrate items array from includes.Entry (recursively)
  if (Array.isArray(entry.fields.items) && includes.Entry) {
    entry.fields.items = entry.fields.items.map(linkedItem => {
      if (linkedItem.sys && linkedItem.sys.id) {
        const hydrated = includes.Entry.find(e => e.sys.id === linkedItem.sys.id);
        if (hydrated) {
          hydrateEntry(hydrated, includes);
          return hydrated;
        }
      }
      return linkedItem;
    });
  }

  // Hydrate menuItem array from includes.Entry (recursively)
  if (Array.isArray(entry.fields.menuItem) && includes.Entry) {
    entry.fields.menuItem = entry.fields.menuItem.map(linkedItem => {
      if (linkedItem.sys && linkedItem.sys.id) {
        const hydrated = includes.Entry.find(e => e.sys.id === linkedItem.sys.id);
        if (hydrated) {
          hydrateEntry(hydrated, includes);
          return hydrated;
        }
      }
      return linkedItem;
    });
  }

  // Rewrite all Contentful asset URLs in the entry to use our API Gateway endpoint
  rewriteUrlsInObject(entry);

  return entry;
}
